import { GetServerSideProps } from "next";
import { useState, useEffect } from "react";
import Head from "next/head";

import { ReceiptView } from "@/components/receipt/ReceiptView";

import { Receipt, ExtendedViewItem } from "@/types/receipt";

import { fetchReceipt, fetchDemoReceipt } from "@/api/receiptFetchers";
import { fetchClientTemplate } from "@/api/templates";
import { fetchMerchantLogo } from "@/api/assets";
import { fetchExtendedViewServices } from "@/api/extendedServices";
import { fetchReceiptServices } from "@/api/receiptServices";
import { fetchStoreLocation, cleanStoreLocationUrl } from "@/api/locations";
import {
  DEFAULT_CLIENT_TEMPLATE,
  DEFAULT_RECEIPT_SERVICES,
} from "@/api/client";

import {
  generateReceiptBarcode,
  generateDiscountBarcodes,
  generateTicketBarcodes,
} from "@/utils/barCodes";
import { fakePayload } from "@/utils/fakePayload";
import { getTemplate, templateRegistry } from "@/templates/registry";
import { TemplateProvider } from "@/contexts/TemplateContext";
import devStyles from "../DevTemplateSwitcher.module.css";
import { config } from "@/config";
import {
  transformExtendedViewServices,
  preprocessExtendedViewItems,
} from "@/utils/extendedViewTransform";
import { fetchExtendedViewImages } from "@/utils/extendedViewImages";
import { sanitizeProps } from "@/utils/serialization";

interface HomeProps {
  receipt: Receipt;
  discountBarcodeImages: string[];
  ticketBarcodeImages: { [articleIndex: string]: string[] };
  mainReceiptBarcodeImage: string;
  lang: string;
  template: string;
  logoReference: string | null;
  user_id: string;
  receipt_id: string;
  isDemo?: boolean;
  isElectronicCopy?: boolean;
  extended_view_items?: ExtendedViewItem[];
  extended_view_images?: { [key: string]: string };
  email?: string | null;
  phoneNumber?: string | null;
  storeLocationUrl?: string | null;
}

const Home = (props: HomeProps) => {
  const { template, receipt, isDemo } = props;
  const [currentTemplate, setCurrentTemplate] = useState(template);

  useEffect(() => {
    setCurrentTemplate(template);
  }, [template]);

  const selectedTemplate = getTemplate(currentTemplate);
  const isDev = process.env.NODE_ENV === "development";

  const pageTitle = receipt.merchant.purchase_location?.store_name
    ? `${receipt.merchant.purchase_location.store_name} Receipt viewer`
    : `Receipt: ${receipt.receipt_number}`;

  return (
    <TemplateProvider templateId={currentTemplate}>
      <Head>
        <title>{pageTitle}</title>
      </Head>
      {isDev && (
        <div className={devStyles.container}>
          <label htmlFor="template-switcher" className={devStyles.label}>
            Template:
          </label>
          <select
            id="template-switcher"
            value={currentTemplate}
            onChange={(e) => setCurrentTemplate(e.target.value)}
            className={devStyles.select}
          >
            {Object.entries(templateRegistry).map(([id, t]) => (
              <option key={id} value={id}>
                {t.name}
              </option>
            ))}
          </select>
        </div>
      )}
      <selectedTemplate.Component>
        {isDemo && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              zIndex: 1000,
              pointerEvents: "none",
              overflow: "hidden",
            }}
          >
            {/* Main large central watermark */}
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%) rotate(-30deg)",
                fontSize: "clamp(32px, 8vw, 64px)",
                color: "rgba(255, 0, 0, 0.12)",
                fontWeight: "bold",
                textTransform: "uppercase",
                whiteSpace: "nowrap",
                letterSpacing: "0.1em",
              }}
            >
              Demo Receipt
            </div>

            {/* Top-right corner indicator */}
            <div
              style={{
                position: "absolute",
                top: "5%",
                right: "5%",
                transform: "rotate(-15deg)",
                fontSize: "clamp(12px, 2vw, 18px)",
                color: "rgba(255, 0, 0, 0.2)",
                fontWeight: "bold",
                textTransform: "uppercase",
                whiteSpace: "nowrap",
                border: "2px solid rgba(255, 0, 0, 0.2)",
                padding: "4px 8px",
                borderRadius: "4px",
              }}
            >
              Demo
            </div>

            {/* Bottom-left corner indicator */}
            <div
              style={{
                position: "absolute",
                bottom: "5%",
                left: "5%",
                transform: "rotate(-15deg)",
                fontSize: "clamp(12px, 2vw, 18px)",
                color: "rgba(255, 0, 0, 0.2)",
                fontWeight: "bold",
                textTransform: "uppercase",
                whiteSpace: "nowrap",
                border: "2px solid rgba(255, 0, 0, 0.2)",
                padding: "4px 8px",
                borderRadius: "4px",
              }}
            >
              Demo
            </div>
          </div>
        )}
        <ReceiptView {...props} />
      </selectedTemplate.Component>
    </TemplateProvider>
  );
};

export default Home;

const getReceiptData = async (
  user_id: string,
  receipt_id: string,
  forwardedHost: string | string[] | undefined
): Promise<Receipt> => {
  const isDemoDomain =
    typeof forwardedHost === "string" && forwardedHost.includes("demo");

  if (config.isLocalFake) {
    return fakePayload;
  } else if (isDemoDomain || config.isLocalDemo) {
    return await fetchDemoReceipt(user_id, receipt_id);
  } else {
    return await fetchReceipt(user_id, receipt_id);
  }
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const {
    user_id,
    zeipt_receipt_transnr,
    email,
    phone_number,
    open_service_scope,
    application_client_service_scope,
  } = context.query;
  const forwardedHost = context.req.headers["x-forwarded-host"];

  // Validate required parameters
  if (
    typeof user_id !== "string" ||
    typeof zeipt_receipt_transnr !== "string" ||
    !user_id ||
    !zeipt_receipt_transnr
  ) {
    return { notFound: true };
  }

  try {
    const receipt = await getReceiptData(
      user_id,
      zeipt_receipt_transnr,
      forwardedHost
    );

    // Fetch receipt services first to check permissions
    const receiptServices = await fetchReceiptServices(
      user_id,
      zeipt_receipt_transnr
    ).catch((error) => {
      console.warn("Failed to fetch receipt services, using defaults:", error);
      return DEFAULT_RECEIPT_SERVICES;
    });

    // Parallelize all operations that depend on receipt
    const [
      clientTemplate,
      logoReference,
      storeLocationResponse,
      mainReceiptBarcodeImage,
      discountBarcodeImages,
      ticketBarcodeImages,
    ] = await Promise.all([
      fetchClientTemplate(receipt.merchant.client_id).catch((error) => {
        console.warn("Failed to fetch client template, using defaults:", error);
        return DEFAULT_CLIENT_TEMPLATE;
      }),
      fetchMerchantLogo(receipt.merchant.client_id).catch((error) => {
        console.warn("Failed to fetch merchant logo:", error);
        return null;
      }),
      receipt.merchant.purchase_location?.store_number
        ? fetchStoreLocation(
            receipt.merchant.client_id,
            receipt.merchant.purchase_location.store_number
          ).catch((error) => {
            console.warn("Failed to fetch store location:", error);
            return null;
          })
        : Promise.resolve(null),
      generateReceiptBarcode(receipt).catch((error) => {
        console.warn("Failed to generate receipt barcode:", error);
        return "";
      }),
      generateDiscountBarcodes(receipt).catch((error) => {
        console.warn("Failed to generate discount barcodes:", error);
        return [];
      }),
      generateTicketBarcodes(receipt).catch((error) => {
        console.warn("Failed to generate ticket barcodes:", error);
        return {};
      }),
    ]);

    const lang = (clientTemplate.language_code ?? "eng").toLowerCase();
    const template = clientTemplate.receipt_template_id ?? "default";
    const storeLocationUrl = storeLocationResponse?.store_location
      ? cleanStoreLocationUrl(storeLocationResponse.store_location)
      : null;

    // Fetch extended view services
    let extendedViewItems: ExtendedViewItem[] = [];
    let extendedViewImages: { [key: string]: string } = {};

    // open_service_scope is valid if not provided or if it is true
    const isOpenServiceScopeValid =
      open_service_scope === "true" || open_service_scope === undefined;

    // Check if application_client_service_scope has a valid value
    const hasValidClientServiceScope =
      typeof application_client_service_scope === "string" &&
      application_client_service_scope.trim() !== "";

    // Determine if we should use client service scope
    const shouldUseClientServiceScope =
      isOpenServiceScopeValid &&
      hasValidClientServiceScope &&
      receiptServices.includes("allow_client_service_scope");

    if (isOpenServiceScopeValid) {
      try {
        const extendedServices = await fetchExtendedViewServices(
          user_id,
          zeipt_receipt_transnr,
          undefined, // No options needed for this endpoint
          shouldUseClientServiceScope
            ? application_client_service_scope
            : undefined
        );
        extendedViewItems = preprocessExtendedViewItems(
          transformExtendedViewServices(extendedServices, lang)
        );
        extendedViewImages = await fetchExtendedViewImages(extendedViewItems);
      } catch (error) {
        console.warn("Failed to fetch extended view services:", error);
      }
    }

    return {
      props: sanitizeProps({
        user_id,
        receipt_id: zeipt_receipt_transnr,
        receipt,
        discountBarcodeImages,
        mainReceiptBarcodeImage,
        ticketBarcodeImages,
        lang,
        template,
        logoReference,
        isElectronicCopy: receipt.electronic_copy,
        extended_view_items: extendedViewItems,
        extended_view_images: extendedViewImages,
        email: typeof email === "string" ? email : null,
        phoneNumber: typeof phone_number === "string" ? phone_number : null,
        storeLocationUrl,
      }),
    };
  } catch (error) {
    console.error("Error in getServerSideProps:", error);
    return { notFound: true };
  }
};
